import { KitPopulationClient, LabClient, LabReadModel, PopulationSearchResultModel } from '@app/api2/api';
import SemanticField from '@app/components/SemanticField';
import { ErrorMessage, Field, Formik } from 'formik';
import * as React from 'react';
import * as yup from "yup";
import { Button, Container, Divider, Form, Label, Message, MessageContent, Segment, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow } from 'semantic-ui-react';
import { buildUrl, AppRoutes } from '../AppRoutes';
import { PageHeader } from '@common/PageHeader';
import { sum, values } from 'lodash';
import { BPButton } from '@/components/bp/bpbutton';
import { BPContainer } from '@/components/bp/bpcontainer';

interface IPopulationCreateLocalProps {
}

type PopulationProportion = { id: number, name: string, proportion: number, selected?: boolean }
type PopulationForm = {
    lab: number;
    items: Array<PopulationProportion>;
    name: string;
    useMinForRare?: boolean;
}

export const PopulationCreateLocal: React.FunctionComponent<IPopulationCreateLocalProps> = (props) => {

    const [labs, setLabs] = React.useState<LabReadModel[]>();
    const [populations, setPopulations] = React.useState<PopulationSearchResultModel[]>();
    const client = new KitPopulationClient();
    const labclient = new LabClient();
    const validationSchema: yup.SchemaOf<PopulationForm> = yup.object().shape({
        lab: yup.number().label(window.translation.common.lab).required(),
        name: yup.string().label(window.translation.population.name).required(),
        items: yup.array().of(yup.object().shape({
            id: yup.number().required(),
            name: yup.string().required().label(window.translation.population.name),
            proportion: yup.number().required().min(0).max(1).nullable().label(window.translation.populationView.weight),
            selected: yup.boolean().nullable()
        })).min(1).required().test('popSumEqualsOne', (t, tc) => {
            // console.log(sum(t.filter(x => x.selected).map(x => x.proportion)))
            // console.log(Math.abs(1.0 - sum(t.filter(x => x.selected).map(x => x.proportion))))
            // check if sum of all selected populations is 1, actually check if it is around 1 because of javascript: (0.3+0.6+0.1=0.999999999999)
            return Math.abs(1.0 - sum(t.filter(x => x.selected).map(x => x.proportion))) <= 0.000001 ? true : tc.createError({ message: 'The sum of weights needs to be 1.' });
        }),
        useMinForRare: yup.boolean().nullable()
    });

    React.useEffect(() => {
        async function fetch() {
            let pageData = await labclient.getAll();
            setLabs(pageData.labs)

            let pageData2 = await client.pageData(1000, 1, '', 'asc', '', false);
            setPopulations(pageData2.items)
        }

        fetch();
    }, [])


    const initValues = {
        items: populations?.map(pop => {
            return { id: pop.id, name: pop.name, proportion: 0 }
        }) ?? [],
        name: "",
        lab: labs != null && labs.length > 0 ? labs[0].id : 1,
        useMinForRare: false
    } as PopulationForm

    return <>
        <BPContainer>
            <PageHeader header={window.translation.populationView.createLocalPopulation}/>
            <Segment>
                <Formik initialValues={initValues}
                    validationSchema={validationSchema}
                    enableReinitialize={true}
                    onSubmit={async (values) => {
                        var response = await client.createLocal({
                            labId: values.lab, name: values.name, populations: values.items.filter(x => x.selected), useMinForRare: values.useMinForRare
                        })

                        if (response.success) {
                            window.location.assign(buildUrl(AppRoutes.PopulationDetail, { populationId: response.id }));
                        } else {
                            console.log(response.errors)
                            // this.setState({
                            //     loading: false,
                            //     serverErrors: response.errors,
                            //     showServerErrors: true,
                            // });
                        }
                    }}>
                    {formikProps => (
                        <Form onSubmit={formikProps.handleSubmit}>
                            <SemanticField component={Form.Input} required width={6} name="name" label={window.translation.common.name} />
                            {labs?.length > 1 &&
                                <SemanticField component={Form.Select} required width={6} name="lab" label={window.translation.common.lab} options={labs?.map(l => ({ key: l.id, value: l.id, text: l.name })) ?? []} value={formikProps.values.lab} />
                            }
                            {labs?.length == 0 &&
                                <SemanticField component={Form.Select} required width={6} name="lab" label={window.translation.common.lab} options={labs?.map(l => ({ key: l.id, value: l.id, text: l.name })) ?? []} value={formikProps.values.lab} />
                            }
                            <SemanticField component={Form.Checkbox} width={6} name={"useMinForRare"} label={'Use minimum for rare'} checked={formikProps.values.useMinForRare}/>

                            <div className="field">
                                <label>{window.translation.common.populations}</label>
                                <Table compact basic verticalAlign='middle'>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHeaderCell></TableHeaderCell>
                                            <TableHeaderCell>{window.translation.common.population}</TableHeaderCell>
                                            <TableHeaderCell>{window.translation.populationView.weight}</TableHeaderCell>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {formikProps.values?.items.map((p, idx) => (
                                            <TableRow key={p.id}>
                                                <TableCell>
                                                    <Field id={`items_${idx}_selected`} name={`items['${idx}'].selected`} type={'checkbox'}></Field>
                                                </TableCell>
                                                <TableCell>
                                                    <label htmlFor={`items_${idx}_selected`}>
                                                        {p.name}
                                                    </label>
                                                </TableCell>
                                                <TableCell width={12}>
                                                    <SemanticField component={Form.Input} type={'number'} required width={6} name={`items['${idx}'].proportion`} />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                            <ErrorMessage name="items">
                                {message => (
                                    <Message negative>
                                        <MessageContent>
                                            {(typeof message == 'string' ? message : (message as Array<PopulationProportion>).map(m => m.proportion))}
                                        </MessageContent>
                                    </Message>
                                )}
                            </ErrorMessage>
                            <Divider />
                            <BPButton type='submit'>{window.translation.common.save}</BPButton>
                        </Form>
                    )}
                </Formik>
            </Segment>
        </BPContainer>

    </>;
};
